import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { logBreak, logBreakEnd, getBreakTimeRemaining } from '@features/admin/api/complianceServices';

const BREAK_REQUIRED_AFTER = 2 * 3600; // 2 hours for DMV compliance (Ohio OAC 4501-7)
const MIN_BREAK_DURATION = 10 * 60; // 10 minutes minimum per Ohio OAC 4501-7

export interface BreakHistoryItem {
  timestamp: string;
  duration: number;
  isMandatory: boolean;
}

interface UseComplianceBreakManagerProps {
  userId: string | undefined;
  sessionId: string | null;
  sessionTime: number;
  sessionTimeRef: React.MutableRefObject<number>;
  sessionTimer: any;
  lastBreakTimeRef?: React.MutableRefObject<number>;
}

export const useComplianceBreakManager = ({
  userId,
  sessionId,
  sessionTime,
  sessionTimeRef,
  sessionTimer,
  lastBreakTimeRef
}: UseComplianceBreakManagerProps) => {
  const BREAK_STATE_KEY = sessionId ? `fastrack_break_state_${sessionId}` : null;

  const [isOnBreak, setIsOnBreak] = useState(false);
  const [isBreakMandatory, setIsBreakMandatory] = useState(false);
  const [breakStartTime, setBreakStartTime] = useState<number | null>(null);
  const [breakHistory, setBreakHistory] = useState<BreakHistoryItem[]>([]);
  const [restoredKey, setRestoredKey] = useState<string | null>(null);
  const [breakTime, setBreakTime] = useState<number>(600);

  const breakRequiredTrackedRef = useRef(false);

  const currentBreakDuration = breakStartTime
    // eslint-disable-next-line react-hooks/purity
    ? Math.floor((Date.now() - breakStartTime) / 1000)
    : 0;

  const isBreakDue = sessionTime - (lastBreakTimeRef?.current || 0) >= BREAK_REQUIRED_AFTER;
  const isBreakMinimumMet = currentBreakDuration >= MIN_BREAK_DURATION;
  const timeUntilBreakRequired = Math.max(0, BREAK_REQUIRED_AFTER - (sessionTime - (lastBreakTimeRef?.current || 0)));

  // Restore break state from localStorage when sessionId changes
  useEffect(() => {
    if (BREAK_STATE_KEY) {
      try {
        const saved = localStorage.getItem(BREAK_STATE_KEY);
        if (saved) {
          const state = JSON.parse(saved);
          if (state.isOnBreak && state.breakStartTime) {
            setIsOnBreak(true);
            setBreakStartTime(state.breakStartTime);
            setIsBreakMandatory(state.isBreakMandatory);
          }
        }
      } catch (error) {
        console.error('Failed to restore break state:', error);
      }
      setRestoredKey(BREAK_STATE_KEY);
    } else {
      setRestoredKey(null);
    }
  }, [BREAK_STATE_KEY]);

  // Persist break state to localStorage whenever it changes
  useEffect(() => {
    if (BREAK_STATE_KEY && restoredKey === BREAK_STATE_KEY) {
      try {
        if (isOnBreak && breakStartTime) {
          localStorage.setItem(BREAK_STATE_KEY, JSON.stringify({
            isOnBreak,
            breakStartTime,
            isBreakMandatory
          }));
        } else {
          localStorage.removeItem(BREAK_STATE_KEY);
        }
      } catch (error) {
        console.error('Failed to persist break state:', error);
      }
    }
  }, [isOnBreak, breakStartTime, isBreakMandatory, restoredKey, BREAK_STATE_KEY]);

  const startBreak = useCallback(async () => {
    console.log('[useComplianceBreakManager] startBreak closure state:', { userId, sessionId });
    setBreakTime(600);

    if (sessionId && userId) {
      try {
        await logBreak(userId, sessionId, {
          reason: 'mandatory'
        });

        const breakInfo = await getBreakTimeRemaining(userId, sessionId);
        console.log('[useComplianceBreakManager] breakInfo retrieved', breakInfo);
        setBreakTime(breakInfo.remainingSeconds);
      } catch (err) {
        console.error('Error handling break:', err);
        setBreakTime(600);
      }
    } else {
      console.warn('[useComplianceBreakManager] skipping service calls in startBreak (missing id)');
    }

    if (!isOnBreak) {
      setIsOnBreak(true);
      setBreakStartTime(Date.now());
      setIsBreakMandatory(true);
      breakRequiredTrackedRef.current = false;
    }
    sessionTimer.pauseTimer();
  }, [sessionId, userId, isOnBreak, sessionTimer]);

  const endBreak = useCallback(async () => {
    console.log('[useComplianceBreakManager] endBreak called', { userId, sessionId });
    try {
      if (sessionId && userId) {
        console.log('[useComplianceBreakManager] calling logBreakEnd...');
        await logBreakEnd(userId, sessionId);
        console.log('[useComplianceBreakManager] logBreakEnd success');

        const breakDuration = breakStartTime ? Math.floor((Date.now() - breakStartTime) / 1000) : 0;

        setBreakHistory(prev => [
          ...prev,
          {
            timestamp: new Date(breakStartTime || Date.now()).toISOString(),
            duration: breakDuration,
            isMandatory: isBreakMandatory
          }
        ]);

        setIsOnBreak(false);
        setBreakStartTime(null);
        setIsBreakMandatory(false);
        setBreakTime(0);

        if (BREAK_STATE_KEY) {
          try {
            localStorage.removeItem(BREAK_STATE_KEY);
          } catch (error) {
            console.error('Failed to clear break state from localStorage:', error);
          }
        }

        sessionTimer.resetSessionTime();
        sessionTimer.resumeTimer();
      } else {
        console.warn('[useComplianceBreakManager] skipping service calls in endBreak (missing id)');

        setIsOnBreak(false);
        setBreakStartTime(null);
        setIsBreakMandatory(false);
        setBreakTime(0);
      }
    } catch (err) {
      console.error('[useComplianceBreakManager] endBreak catch:', err);
      throw err;
    }
  }, [sessionId, userId, breakStartTime, isBreakMandatory, sessionTimer, BREAK_STATE_KEY]);

  useEffect(() => {
    if (isBreakDue && !isOnBreak && !breakRequiredTrackedRef.current) {
      breakRequiredTrackedRef.current = true;
      setIsBreakMandatory(true);
      const currentTime = sessionTimeRef ? sessionTimeRef.current : sessionTime;
      // Triggers startBreak in the provider when required
    }

    if (!isBreakDue) {
      breakRequiredTrackedRef.current = false;
    }
  }, [isBreakDue, isOnBreak, sessionTime, sessionTimeRef]);

  return useMemo(() => ({
    isOnBreak,
    isBreakMandatory,
    breakStartTime,
    breakHistory,
    isBreakDue,
    isBreakMinimumMet,
    currentBreakDuration,
    timeUntilBreakRequired,
    breakTime,
    setBreakTime,
    startBreak,
    endBreak,
    setBreakStartTime,
    setIsOnBreak,
    setIsBreakMandatory
  }), [
    isOnBreak,
    isBreakMandatory,
    breakStartTime,
    breakHistory,
    isBreakDue,
    isBreakMinimumMet,
    currentBreakDuration,
    timeUntilBreakRequired,
    breakTime,
    setBreakTime,
    startBreak,
    endBreak
  ]);
};

export default useComplianceBreakManager;
